/**
 * Manual job attribution — the escape hatch for an invoice the automatic match
 * couldn't place (ENG-2440). This is the affordance: a button owning the dialog
 * and the mutation. The search control lives in `job-search-combobox.tsx` and
 * the search/submission state in `manual-job-match-form.tsx`.
 */
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { useMatchDocumentJob } from "@/accounts-payable/query";
import type { ApDocumentType } from "@/accounts-payable/types";
import { documentTypeLabel } from "@/accounts-payable/utils";
import { ManualJobMatchForm } from "./manual-job-match-form";

/** Render it where a document shows its job — attached or missing — see `canManuallyMatchJob`. */
export const ManualJobMatchButton = ({
  businessId,
  documentId,
  documentType = "invoice",
  agentRunId,
  label = "Match to a job",
  size = "xsPill",
  variant = "secondary",
  className,
}: {
  businessId: string | null;
  documentId: string;
  documentType?: ApDocumentType;
  /** The document's newest run id, used to detect whether the match started a new one. */
  agentRunId: string | null;
  label?: string;
  className?: string;
  size?: "xs" | "xsPill" | "sm";
  variant?: "default" | "secondary" | "tertiary" | "text";
}) => {
  const [open, setOpen] = useState(false);
  /**
   * The mutation lives here rather than in the form: React Query only runs
   * `mutate`-level callbacks while the observer is mounted, and the form
   * unmounts with the dialog.
   */
  const matchJob = useMatchDocumentJob(businessId, documentType);
  const kind = documentTypeLabel(documentType);

  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={className}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          // A match in flight holds the dialog open — escape, the overlay and
          // the close button would otherwise dismiss it mid-request, leaving
          // the outcome with nowhere to land.
          if (matchJob.isPending) return;
          setOpen(next);
        }}
      >
        <DialogContent
          className="max-w-[560px] gap-6"
          description={`Match this ${kind.lower} to a ServiceTitan job`}
        >
          <DialogHeader className="pb-0 text-left">
            <DialogTitle>Match {kind.lower} to a job</DialogTitle>
          </DialogHeader>
          {open && (
            <ManualJobMatchForm
              businessId={businessId}
              documentId={documentId}
              documentType={documentType}
              agentRunId={agentRunId}
              matchJob={matchJob}
              onClose={() => setOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
